import { DragData, DropData, Edge } from "./types";

export function isDragData(
    data: Record<string | symbol, unknown>
): data is DragData & Record<string | symbol, unknown> {
    return (
        typeof data.listId === "string" &&
        typeof data.index === "number" &&
        Array.isArray(data.isLockedItems)
    );
}

export function isDropData(
    data: Record<string | symbol, unknown>
): data is DropData & Record<string | symbol, unknown> {
    return (
        typeof data.listId === "string" &&
        typeof data.index === "number" &&
        (data.edge === "top" || data.edge === "bottom")
    );
}

export function computeFinishIndex(
    startIndex: number,
    destIndex: number,
    edge: Edge
): number {
    const targetIndex = edge === "top" ? destIndex : destIndex + 1;
    return startIndex < targetIndex ? targetIndex - 1 : targetIndex;
}

/**
 * Returns `true` if dropping the source at `finishIndex` would place it
 * immediately before a locked item in the post-reorder array.
 *
 * After moving the source from `startIndex` to `finishIndex`, the item at new
 * index `finishIndex + 1` corresponds to original index:
 *   - `finishIndex` when `finishIndex < startIndex` (items shift right to make
 *     room for the source)
 *   - `finishIndex + 1` otherwise (items beyond the source's path are unshifted)
 */
function wouldLandBeforeLock(
    startIndex: number,
    finishIndex: number,
    isLockedItems: ReadonlyArray<boolean>
): boolean {
    const successorOriginalIndex =
        finishIndex < startIndex ? finishIndex : finishIndex + 1;
    return (
        successorOriginalIndex < isLockedItems.length &&
        isLockedItems[successorOriginalIndex] === true
    );
}

/** Returns `true` if `source` can be reordered to `dest`'s edge. */
export function canPlaceAt(source: DragData, dest: DropData): boolean {
    const finishIndex = computeFinishIndex(source.index, dest.index, dest.edge);
    if (finishIndex === source.index) {
        return false;
    }
    return !wouldLandBeforeLock(
        source.index,
        finishIndex,
        source.isLockedItems
    );
}
